import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Clock,
  Calendar,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Timer,
  AlertCircle,
  Eye,
  Check,
  X,
  Download,
  MapPin,
  User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AttendanceRecordsPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Format local date as YYYY-MM-DD without going through UTC.
  // `.toISOString()` flips eastward-of-UTC dates back one day at
  // late-evening local time — same root cause as the
  // EmployeeManagementPage attendance-calendar bug fixed 2026-02-04.
  const fmtLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Filters - initialize statusFilter from URL params
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return fmtLocal(date);
  });
  const [endDate, setEndDate] = useState(fmtLocal(new Date()));
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [statusFilter, setStatusFilter] = useState(() => {
    return searchParams.get('status') || 'all';
  });
  
  // Review dialog
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  // Correction requests
  const [correctionRequests, setCorrectionRequests] = useState([]);
  const [correctionReviewDialogOpen, setCorrectionReviewDialogOpen] = useState(false);
  const [selectedCorrection, setSelectedCorrection] = useState(null);
  const [correctionReviewNotes, setCorrectionReviewNotes] = useState('');
  const [correctionReviewLoading, setCorrectionReviewLoading] = useState(false);

  const fetchRecords = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedEmployee !== 'all') params.append('user_id', selectedEmployee);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_BASE_URL}/hr/attendance/records?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecords(response.data.records);
    } catch (error) {
      console.error('Error fetching records:', error);
      toast.error('Failed to load attendance records');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedEmployee, statusFilter]);

  const fetchPendingReviews = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/hr/attendance/records?requires_review=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingReviews(response.data.records);
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/hr/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.employees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  const fetchCorrectionRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/hr/attendance/correction-requests?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorrectionRequests(response.data.requests || []);
    } catch (error) {
      // Permission denied is expected for non-HR users
      if (error.response?.status !== 403) {
        console.error('Error fetching correction requests:', error);
      }
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchRecords(), fetchPendingReviews(), fetchEmployees(), fetchCorrectionRequests()]);
  }, [fetchRecords, fetchPendingReviews, fetchEmployees, fetchCorrectionRequests]);

  const handleReview = (record) => {
    setSelectedRecord(record);
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const submitReview = async (approved) => {
    if (!selectedRecord) return;
    
    setReviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/hr/attendance/${selectedRecord.id}/review?reviewer_id=${user.id}`,
        {
          status: approved ? 'approved' : 'rejected',
          notes: reviewNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Attendance ${approved ? 'approved' : 'rejected'}`);
      setReviewDialogOpen(false);
      fetchRecords();
      fetchPendingReviews();
    } catch (error) {
      console.error('Error reviewing attendance:', error);
      toast.error('Failed to submit review');
    } finally {
      setReviewLoading(false);
    }
  };

  // Correction request handlers
  const handleCorrectionReview = (correction) => {
    setSelectedCorrection(correction);
    setCorrectionReviewNotes('');
    setCorrectionReviewDialogOpen(true);
  };

  const submitCorrectionReview = async (action) => {
    if (!selectedCorrection) return;
    
    setCorrectionReviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/hr/attendance/correction-request/${selectedCorrection.id}/review`,
        {
          action: action, // 'approve' or 'reject'
          notes: correctionReviewNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Correction request ${action}d`);
      setCorrectionReviewDialogOpen(false);
      fetchCorrectionRequests();
    } catch (error) {
      console.error('Error reviewing correction request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setCorrectionReviewLoading(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const statusConfig = {
      on_time: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle, label: 'On Time' },
      late: { color: 'bg-yellow-500/20 text-yellow-400', icon: Timer, label: 'Late' },
      half_day: { color: 'bg-orange-500/20 text-orange-400', icon: Clock, label: 'Half Day' },
      absent: { color: 'bg-red-500/20 text-red-400', icon: XCircle, label: 'Absent' },
      on_leave: { color: 'bg-blue-500/20 text-blue-400', icon: Calendar, label: 'On Leave' }
    };

    const config = statusConfig[status] || statusConfig.absent;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const MethodBadge = ({ method }) => {
    const methodConfig = {
      face_recognition: { color: 'bg-green-500/20 text-green-400', label: 'Face' },
      selfie: { color: 'bg-yellow-500/20 text-yellow-400', label: 'Selfie' },
      manual: { color: 'bg-purple-500/20 text-purple-400', label: 'Manual' }
    };

    const config = methodConfig[method] || methodConfig.manual;

    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attendance-records-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Attendance Records</h1>
          <p className="text-wt-text-muted">{records.length} records</p>
        </div>
        {pendingReviews.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500/10 border border-orange-500/30">
            <AlertCircle className="h-5 w-5 text-orange-400" />
            <span className="text-orange-400 font-medium">
              {pendingReviews.length} pending review{pendingReviews.length > 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      <Tabs defaultValue="records" className="w-full">
        <TabsList className="bg-wt-surface border border-wt-border">
          <TabsTrigger value="records" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            All Records
          </TabsTrigger>
          <TabsTrigger value="reviews" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            Pending Reviews ({pendingReviews.length})
          </TabsTrigger>
          <TabsTrigger value="corrections" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            Correction Requests ({correctionRequests.length})
          </TabsTrigger>
        </TabsList>

        {/* All Records Tab */}
        <TabsContent value="records" className="mt-4 space-y-4">
          {/* Filters */}
          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-wt-text-muted">Start Date</Label>
                  <DateInput
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || undefined}
                    className="bg-wt-bg border-wt-border"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-wt-text-muted">End Date</Label>
                  <DateInput
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || undefined}
                    className="bg-wt-bg border-wt-border"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-wt-text-muted">Employee</Label>
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger className="bg-wt-bg border-wt-border">
                      <SelectValue placeholder="All Employees" />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-surface border-wt-border max-h-60">
                      <SelectItem value="all">All Employees</SelectItem>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-wt-text-muted">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="bg-wt-bg border-wt-border">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-surface border-wt-border">
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="on_time">On Time</SelectItem>
                      <SelectItem value="late">Late</SelectItem>
                      <SelectItem value="half_day">Half Day</SelectItem>
                      <SelectItem value="absent">Absent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={fetchRecords}
                    className="w-full bg-wt-gold hover:bg-wt-gold-hover text-black"
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Apply
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Records Table */}
          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-wt-bg border-b border-wt-border">
                    <tr>
                      <th className="text-left p-4 text-xs font-medium text-wt-text-muted">Employee</th>
                      <th className="text-left p-4 text-xs font-medium text-wt-text-muted">Date</th>
                      <th className="text-left p-4 text-xs font-medium text-wt-text-muted">Check-in</th>
                      <th className="text-left p-4 text-xs font-medium text-wt-text-muted">Check-out</th>
                      <th className="text-left p-4 text-xs font-medium text-wt-text-muted">Hours</th>
                      <th className="text-left p-4 text-xs font-medium text-wt-text-muted">Method</th>
                      <th className="text-left p-4 text-xs font-medium text-wt-text-muted">Status</th>
                      <th className="text-left p-4 text-xs font-medium text-wt-text-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-wt-border">
                    {records.map((record) => (
                      <tr key={record.id} className="hover:bg-wt-bg/50">
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-wt-gold/20 flex items-center justify-center">
                              <span className="text-xs font-medium text-wt-gold">
                                {record.user_name?.charAt(0)}
                              </span>
                            </div>
                            <span className="text-sm text-wt-text-primary">{record.user_name}</span>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-wt-text-secondary">{record.date}</td>
                        <td className="p-4 text-sm text-wt-text-primary">
                          {record.check_in_time 
                            ? new Date(record.check_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : '-'
                          }
                        </td>
                        <td className="p-4 text-sm text-wt-text-primary">
                          {record.check_out_time 
                            ? new Date(record.check_out_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : '-'
                          }
                        </td>
                        <td className="p-4 text-sm text-wt-text-primary">
                          {record.total_hours ? `${record.total_hours.toFixed(1)}h` : '-'}
                        </td>
                        <td className="p-4">
                          <MethodBadge method={record.check_in_method} />
                        </td>
                        <td className="p-4">
                          <StatusBadge status={record.status} />
                        </td>
                        <td className="p-4">
                          {record.requires_review && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReview(record)}
                              className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {records.length === 0 && (
                  <div className="text-center py-12 text-wt-text-muted">
                    <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No attendance records found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Reviews Tab */}
        <TabsContent value="reviews" className="mt-4">
          <div className="space-y-3">
            {pendingReviews.length === 0 ? (
              <Card className="bg-wt-surface border-wt-border">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-400 opacity-50" />
                  <p className="text-wt-text-muted">No pending reviews</p>
                </CardContent>
              </Card>
            ) : (
              pendingReviews.map((record) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="bg-wt-surface border-wt-border hover:border-orange-500/30 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          {/* Face Image */}
                          {record.check_in_face_image && (
                            <div className="h-16 w-16 rounded-lg overflow-hidden bg-wt-bg border border-wt-border">
                              <img
                                src={record.check_in_face_image}
                                alt="Attendance"
                                className="h-full w-full object-cover"
                              />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-wt-text-primary">{record.user_name}</p>
                            <p className="text-sm text-wt-text-muted">{record.date}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Clock className="h-3 w-3 text-wt-text-muted" />
                              <span className="text-xs text-wt-text-muted">
                                {record.check_in_time && new Date(record.check_in_time).toLocaleTimeString()}
                              </span>
                              <MapPin className="h-3 w-3 text-wt-text-muted ml-2" />
                              <span className="text-xs text-wt-text-muted">
                                {record.check_in_latitude?.toFixed(4)}, {record.check_in_longitude?.toFixed(4)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={record.status} />
                          <MethodBadge method={record.check_in_method} />
                          <Button
                            size="sm"
                            onClick={() => handleReview(record)}
                            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Review
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Correction Requests Tab */}
        <TabsContent value="corrections" className="mt-4">
          <div className="space-y-4">
            {correctionRequests.length === 0 ? (
              <Card className="bg-wt-surface border-wt-border">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-wt-text-primary mb-2">All Caught Up!</h3>
                  <p className="text-wt-text-muted">No pending correction requests to review.</p>
                </CardContent>
              </Card>
            ) : (
              correctionRequests.map((correction) => (
                <motion.div
                  key={correction.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="bg-wt-surface border border-orange-500/30 hover:border-wt-gold/50 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="h-10 w-10 rounded-full bg-wt-gold/20 flex items-center justify-center">
                              <User className="h-5 w-5 text-wt-gold" />
                            </div>
                            <div>
                              <h3 className="text-wt-text-primary font-medium">{correction.user_name}</h3>
                              <p className="text-xs text-wt-text-muted">
                                Submitted: {new Date(correction.created_at).toLocaleDateString('en-US', { 
                                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 p-3 bg-wt-bg rounded-lg">
                            <div>
                              <p className="text-xs text-wt-text-muted">Date</p>
                              <p className="text-sm font-medium text-wt-text-primary">
                                {new Date(correction.date).toLocaleDateString('en-US', { 
                                  weekday: 'short', month: 'short', day: 'numeric' 
                                })}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-wt-text-muted">Type</p>
                              <p className="text-sm font-medium text-wt-gold">
                                {correction.correction_type === 'check_in' ? 'Check-In' : 'Check-Out'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-wt-text-muted">Requested Time</p>
                              <p className="text-sm font-medium text-wt-text-primary">{correction.requested_time}</p>
                            </div>
                            <div>
                              <p className="text-xs text-wt-text-muted">Reason</p>
                              <p className="text-sm text-wt-text-secondary truncate" title={correction.reason}>
                                {correction.reason}
                              </p>
                            </div>
                          </div>
                          {correction.screenshot_url && (
                            <a 
                              href={correction.screenshot_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-blue-400 hover:text-blue-300 mt-2 inline-flex items-center gap-1"
                            >
                              <Eye className="h-4 w-4" />
                              View Proof/Screenshot
                            </a>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            size="sm"
                            onClick={() => handleCorrectionReview(correction)}
                            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Review
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">
              Review Attendance: {selectedRecord?.user_name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Attendance Details */}
            <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-wt-bg border border-wt-border">
              <div>
                <p className="text-xs text-wt-text-muted">Date</p>
                <p className="text-sm font-medium text-wt-text-primary">{selectedRecord?.date}</p>
              </div>
              <div>
                <p className="text-xs text-wt-text-muted">Check-in Time</p>
                <p className="text-sm font-medium text-wt-text-primary">
                  {selectedRecord?.check_in_time && new Date(selectedRecord.check_in_time).toLocaleTimeString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-wt-text-muted">Method</p>
                <MethodBadge method={selectedRecord?.check_in_method} />
              </div>
              <div>
                <p className="text-xs text-wt-text-muted">Status</p>
                <StatusBadge status={selectedRecord?.status} />
              </div>
            </div>

            {/* Face Image */}
            {selectedRecord?.check_in_face_image && (
              <div className="space-y-2">
                <Label>Captured Image</Label>
                <div className="h-48 rounded-lg overflow-hidden bg-wt-bg border border-wt-border">
                  <img
                    src={selectedRecord.check_in_face_image}
                    alt="Attendance"
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            )}

            {/* Location */}
            <div className="p-3 rounded-lg bg-wt-bg border border-wt-border">
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-wt-text-muted" />
                <span className="text-wt-text-primary">
                  {selectedRecord?.check_in_latitude?.toFixed(6)}, {selectedRecord?.check_in_longitude?.toFixed(6)}
                </span>
              </div>
            </div>

            {/* Review Notes */}
            <div className="space-y-2">
              <Label>Review Notes</Label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add notes (optional)"
                className="bg-wt-bg border-wt-border"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => submitReview(false)}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              disabled={reviewLoading}
            >
              <X className="h-4 w-4 mr-1" />
              Reject
            </Button>
            <Button
              onClick={() => submitReview(true)}
              className="bg-green-500 hover:bg-green-600 text-white"
              disabled={reviewLoading}
            >
              <Check className="h-4 w-4 mr-1" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Correction Review Dialog */}
      <Dialog open={correctionReviewDialogOpen} onOpenChange={setCorrectionReviewDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">
              Review Correction Request: {selectedCorrection?.user_name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Correction Details */}
            <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-wt-bg border border-wt-border">
              <div>
                <p className="text-xs text-wt-text-muted">Date</p>
                <p className="text-sm font-medium text-wt-text-primary">
                  {selectedCorrection?.date && new Date(selectedCorrection.date).toLocaleDateString('en-US', { 
                    weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' 
                  })}
                </p>
              </div>
              <div>
                <p className="text-xs text-wt-text-muted">Correction Type</p>
                <p className="text-sm font-medium text-wt-gold">
                  {selectedCorrection?.correction_type === 'check_in' ? 'Check-In Time' : 'Check-Out Time'}
                </p>
              </div>
              <div>
                <p className="text-xs text-wt-text-muted">Requested Time</p>
                <p className="text-sm font-medium text-wt-text-primary">{selectedCorrection?.requested_time}</p>
              </div>
              <div>
                <p className="text-xs text-wt-text-muted">Submitted</p>
                <p className="text-sm text-wt-text-secondary">
                  {selectedCorrection?.created_at && new Date(selectedCorrection.created_at).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
            
            <div className="p-4 rounded-lg bg-orange-500/5 border border-orange-500/20">
              <p className="text-xs text-wt-text-muted mb-1">Reason for Correction</p>
              <p className="text-sm text-wt-text-primary">{selectedCorrection?.reason}</p>
            </div>

            {selectedCorrection?.screenshot_url && (
              <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <a 
                  href={selectedCorrection.screenshot_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  View Attached Proof/Screenshot
                </a>
              </div>
            )}

            {/* Review Notes */}
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Review Notes (Optional)</Label>
              <Textarea
                value={correctionReviewNotes}
                onChange={(e) => setCorrectionReviewNotes(e.target.value)}
                placeholder="Add notes for approval/rejection reason..."
                className="bg-wt-bg border-wt-border"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => submitCorrectionReview('reject')}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              disabled={correctionReviewLoading}
            >
              <X className="h-4 w-4 mr-1" />
              Reject
            </Button>
            <Button
              onClick={() => submitCorrectionReview('approve')}
              className="bg-green-500 hover:bg-green-600 text-white"
              disabled={correctionReviewLoading}
            >
              <Check className="h-4 w-4 mr-1" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendanceRecordsPage;
